
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';

type PrivateRouteProps = {
  requiredRole?: 'customer' | 'administrator';
  requireApproval?: boolean;
  allowSuspended?: boolean;
};

export default function PrivateRoute({ 
  requiredRole, 
  requireApproval = true,
  allowSuspended = false
}: PrivateRouteProps) {
  const { user, profile, isLoading, isAdmin, isApproved } = useAuth();
  const location = useLocation();
  
  useEffect(() => {
    console.log("PrivateRoute check:", { 
      user: !!user, 
      profile: !!profile, 
      isApproved, 
      requireApproval,
      suspended: profile?.suspended,
      path: location.pathname
    });
  }, [user, profile, isApproved, requireApproval, location]);

  // Show loading state while checking authentication
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // If not logged in at all, redirect to login
  if (!user) {
    console.log("Not logged in, redirecting to login");
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }
  
  // If user is suspended and this route doesn't explicitly allow suspended users, redirect to suspended page
  if (profile?.suspended && !allowSuspended && location.pathname !== '/auth/suspended') {
    console.log("User is suspended, redirecting to suspended account page");
    return <Navigate to="/auth/suspended" replace />;
  }

  // If approval is required but user is not approved, redirect to pending page
  if (requireApproval && !isApproved && location.pathname !== '/auth/pending-approval') {
    console.log("User not approved, redirecting to pending approval");
    return <Navigate to="/auth/pending-approval" replace />;
  }

  // If role is required and user doesn't have it
  if (requiredRole === 'administrator' && !isAdmin) {
    console.log("User is not an admin, redirecting to members page");
    return <Navigate to="/members" replace />;
  }

  // All checks passed, render the protected route
  return <Outlet />;
}
